import { authenticate, refreshToken as refreshTokenApi, type TokenResponse } from './api';
import { setUnauthorizedHandler } from './authFetch';
import { userStore } from './store';

export const REFRESH_TOKEN_STORAGE_KEY = 'pay-roadrunner.refreshToken';
const REFRESH_LEEWAY_MS = 60_000;

export interface AuthApiClient {
  authenticate: typeof authenticate;
  refreshToken: typeof refreshTokenApi;
}

interface AuthServiceOptions {
  api?: AuthApiClient;
  storage?: Storage;
  redirectToLogin?: () => void;
}

export class AuthService {
  private api: AuthApiClient;
  private storage: Storage;
  private redirectToLogin: () => void;
  private refreshTimer: ReturnType<typeof window.setTimeout> | null = null;
  private initPromise: Promise<boolean> | null = null;

  constructor({ api = { authenticate, refreshToken: refreshTokenApi }, storage = window.localStorage, redirectToLogin = () => window.location.assign('/login') }: AuthServiceOptions = {}) {
    this.api = api;
    this.storage = storage;
    this.redirectToLogin = redirectToLogin;
    window.addEventListener('focus', this.handleFocus);
  }

  async login(email: string, password: string): Promise<void> {
    const response = await this.api.authenticate(email, password);
    this.applyTokenResponse(response);
  }

  logout({ redirect = true }: { redirect?: boolean } = {}): void {
    this.stopRefreshTimer();
    userStore.clearAuth();
    userStore.setInitialising(false);
    this.storage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
    if (redirect) this.redirectToLogin();
  }

  initFromStorage(): Promise<boolean> {
    this.initPromise ??= this.runInitFromStorage().finally(() => {
      this.initPromise = null;
    });
    return this.initPromise;
  }

  async refreshToken(): Promise<void> {
    const storedRefreshToken = this.storage.getItem(REFRESH_TOKEN_STORAGE_KEY);
    if (!storedRefreshToken) {
      this.logout({ redirect: true });
      return;
    }

    try {
      const response = await this.api.refreshToken(storedRefreshToken);
      this.applyTokenResponse(response);
    } catch (error) {
      this.logout({ redirect: true });
      throw error;
    }
  }

  dispose(): void {
    this.stopRefreshTimer();
    window.removeEventListener('focus', this.handleFocus);
  }

  private async runInitFromStorage(): Promise<boolean> {
    userStore.setInitialising(true);
    const storedRefreshToken = this.storage.getItem(REFRESH_TOKEN_STORAGE_KEY);

    if (!storedRefreshToken) {
      userStore.clearAuth();
      userStore.setInitialising(false);
      return false;
    }

    try {
      const response = await this.api.refreshToken(storedRefreshToken);
      this.applyTokenResponse(response);
      return true;
    } catch {
      this.logout({ redirect: false });
      return false;
    } finally {
      userStore.setInitialising(false);
    }
  }

  private applyTokenResponse(response: TokenResponse): void {
    userStore.setAuth({ token: response.token, expiresAt: response.expiresAt });
    userStore.setInitialising(false);
    this.storage.setItem(REFRESH_TOKEN_STORAGE_KEY, response.refreshToken);
    this.startRefreshTimer();
  }

  private startRefreshTimer(): void {
    this.stopRefreshTimer();
    if (!userStore.expiresAt) return;

    const delay = Math.max(userStore.expiresAt * 1000 - Date.now() - REFRESH_LEEWAY_MS, 0);
    this.refreshTimer = window.setTimeout(() => {
      void this.refreshToken();
    }, delay);
  }

  private stopRefreshTimer(): void {
    if (!this.refreshTimer) return;
    window.clearTimeout(this.refreshTimer);
    this.refreshTimer = null;
  }

  private handleFocus = (): void => {
    if (userStore.isAuthenticated) {
      this.startRefreshTimer();
    }
  };
}

export const authService = new AuthService();
setUnauthorizedHandler(() => authService.refreshToken());
